import api from "@/lib/api";

export interface AdminPayment {
  id: string;
  /** ID do pagamento (para refund; em linhas expandidas, diferente de id) */
  paymentId?: string;
  userId: string;
  userEmail: string;
  userName?: string | null;
  gameId?: string | null;
  gameName?: string | null;
  gameDate?: string | null;
  teamId?: string | null;
  teamName?: string | null;
  teamSlug?: string | null;
  teamPhoto?: string | null;
  gameParticipantId?: string | null;
  provider: string;
  method: string;
  status: string;
  amount: number;
  fee?: number | null;
  platformFee?: number | null;
  netAmount?: number | null;
  providerPaymentId?: string | null;
  /** ID da operação no MP (para link banking/balance/movements/{id}/detail) */
  providerMovementId?: string | null;
  providerError?: string | null;
  providerStatus?: string | null;
  providerStatusDetail?: string | null;
  providerErrorMessage?: string | null;
  createdAt: string;
  paidAt?: string | null;
  canceledAt?: string | null;
  refundedAt?: string | null;
  /** Pagamento panelinha (um pagador para vários ingressos) */
  isPanelinha?: boolean;
  /** Nomes dos participantes que receberam os ingressos */
  participantNames?: string[];
  /** ID do participante (linha expandida) - para reembolso parcial */
  participantId?: string;
  /** Nome do participante (linha expandida) */
  participantName?: string;
  /** Nome de quem pagou (quando diferente do participante) */
  paidByName?: string;
  /** Vaga foi transferida — reembolso bloqueado */
  slotTransferred?: boolean;
  slotTransferredAt?: string | null;
  canRefund?: boolean;
}

export interface AdminPaymentsList {
  items: AdminPayment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AdminPaymentsParams {
  page?: number;
  limit?: number;
  status?: string;
  method?: string;
  provider?: string;
  q?: string;
  paidAtFrom?: string;
  paidAtTo?: string;
  refundedAtFrom?: string;
  refundedAtTo?: string;
}

export interface AdminWithdrawal {
  id: string;
  teamId: string;
  teamName: string;
  teamSlug: string;
  teamPhoto?: string | null;
  teamBalance: number;
  requestedByName?: string | null;
  requestedByEmail?: string | null;
  amount: number;
  status: string;
  pixKey?: string;
  pixKeyType?: string;
  requestedAt: string;
  processedAt?: string;
  providerTransactionId?: string;
   /** URL do comprovante de transferência (quando admin faz upload manual). */
  receiptUrl?: string;
}

export interface AdminEarnings {
  totalEarnings: number;
  earningsThisMonth: number;
  earningsThisYear: number;
  paidPaymentsCount: number;
}

export interface AdminDashboard {
  totalEarnings: number;
  earningsThisMonth: number;
  earningsThisYear: number;
  paidPaymentsCount: number;
  totalPaymentVolume: number;
  paymentVolumeThisMonth: number;
  usersCount: number;
  usersThisMonth: number;
  teamsCount: number;
  teamsThisMonth: number;
  gamesCount: number;
  gamesOpenCount: number;
  gamesThisMonth: number;
  participantsCount: number;
  paymentsByStatus: Record<string, number>;
  withdrawalsCount: number;
  withdrawalsPendingCount: number;
  withdrawalsCompletedCount: number;
  withdrawalsCompletedAmount: number;
}

export interface AdminTeamUser {
  userId: string;
  name?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  cpf?: string | null;
}

export interface AdminTeamRow {
  teamId: string;
  timeName: string;
  timeSlug: string;
  photo?: string | null;
  owner: AdminTeamUser;
  administrators: AdminTeamUser[];
  membersCount: number;
  gamesCount: number;
  totalMoved: number;
  availableToWithdraw: number;
}

export interface RefundDebugInfo {
  paymentId: string;
  status: string;
  providerPaymentId: string | null;
  providerPaymentStatus: string | null;
  providerPaymentStatusDetail: string | null;
  providerRefundable: boolean | null;
  providerFetchError: string | null;
  providerRefundId: string | null;
  providerError: string | null;
  refundedAt: string | null;
  queueJob: {
    jobId: string;
    exists: boolean;
    state?: string;
    attemptsMade?: number;
    failedReason?: string;
    stacktrace?: string[];
    timestamp?: number;
    processedOn?: number | null;
    finishedOn?: number | null;
  };
}

export const adminService = {
  async checkIsAdmin(): Promise<boolean> {
    try {
      await api.get("/admin/check");
      return true;
    } catch {
      return false;
    }
  },

  async testSentryServer(): Promise<{ success: true; message: string }> {
    const response = await api.post<{ success: true; message: string }>("/admin/sentry/test");
    return response.data;
  },

  async getEarnings(): Promise<AdminEarnings> {
    const response = await api.get<AdminEarnings>("/admin/earnings");
    return response.data;
  },

  async getDashboard(): Promise<AdminDashboard> {
    const response = await api.get<AdminDashboard>("/admin/dashboard");
    return response.data;
  },

  async listTeams(): Promise<AdminTeamRow[]> {
    const response = await api.get<AdminTeamRow[]>("/admin/teams");
    return response.data;
  },

  async listPayments(params?: AdminPaymentsParams): Promise<AdminPaymentsList> {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set("page", String(params.page));
    if (params?.limit != null) searchParams.set("limit", String(params.limit));
    if (params?.status) searchParams.set("status", params.status);
    if (params?.method) searchParams.set("method", params.method);
    if (params?.provider) searchParams.set("provider", params.provider);
    if (params?.q?.trim()) searchParams.set("q", params.q.trim());
    if (params?.paidAtFrom) searchParams.set("paidAtFrom", params.paidAtFrom);
    if (params?.paidAtTo) searchParams.set("paidAtTo", params.paidAtTo);
    if (params?.refundedAtFrom) searchParams.set("refundedAtFrom", params.refundedAtFrom);
    if (params?.refundedAtTo) searchParams.set("refundedAtTo", params.refundedAtTo);
    const query = searchParams.toString();
    const response = await api.get<AdminPaymentsList>(
      `/admin/payments${query ? `?${query}` : ""}`,
    );
    return response.data;
  },

  async refundPayment(
    paymentId: string,
    body?: { gameParticipantId?: string },
  ): Promise<{ paymentId: string; enqueued: boolean }> {
    const response = await api.patch<{ paymentId: string; enqueued: boolean }>(
      `/admin/payments/${paymentId}/refund`,
      body ?? {},
    );
    return response.data;
  },

  async getRefundDebug(paymentId: string): Promise<RefundDebugInfo> {
    const response = await api.get<RefundDebugInfo>(`/admin/payments/${paymentId}/refund-debug`);
    return response.data;
  },

  async listWithdrawals(status?: string): Promise<AdminWithdrawal[]> {
    const params = status ? { status } : {};
    const response = await api.get<AdminWithdrawal[]>("/admin/withdrawals", {
      params,
    });
    return response.data;
  },

  async processWithdrawalViaMp(withdrawalId: string): Promise<void> {
    await api.patch(`/admin/withdrawals/${withdrawalId}/process-mp`);
  },

  async markWithdrawalComplete(withdrawalId: string): Promise<void> {
    await api.patch(`/admin/withdrawals/${withdrawalId}/complete`);
  },

  async cancelWithdrawal(withdrawalId: string): Promise<void> {
    await api.patch(`/admin/withdrawals/${withdrawalId}/cancel`);
  },

  async listPushSubscriptions(): Promise<
    { userId: string; userEmail: string; userName: string | null; subscribedAt: string }[]
  > {
    const response = await api.get<
      { userId: string; userEmail: string; userName: string | null; subscribedAt: string }[]
    >('/admin/push/subscriptions');
    return response.data;
  },

  async sendPush(body: {
    userId?: string;
    userIds?: string[];
    title: string;
    body: string;
    url?: string;
  }): Promise<{ sent: number }> {
    const response = await api.post<{ sent: number }>('/admin/push/send', body);
    return response.data;
  },

  async backfillTeamMembers(dryRun: boolean): Promise<{
    confirmedWithTeam: number;
    alreadyMembers: number;
    toAdd: number;
    added: number;
    dryRun: boolean;
    preview?: { userId: string; teamName: string; role: string }[];
  }> {
    const response = await api.post<{
      confirmedWithTeam: number;
      alreadyMembers: number;
      toAdd: number;
      added: number;
      dryRun: boolean;
      preview?: { userId: string; teamName: string; role: string }[];
    }>(`/admin/backfill/team-members?dryRun=${dryRun ? "true" : "false"}`);
    return response.data;
  },

  async uploadWithdrawalReceipt(
    withdrawalId: string,
    file: File,
  ): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    await api.patch(`/admin/withdrawals/${withdrawalId}/receipt`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  async listLabsSubscriptions(q?: string): Promise<AdminLabsSubscription[]> {
    const response = await api.get<AdminLabsSubscription[]>("/admin/labs/subscriptions", {
      params: q?.trim() ? { q: q.trim() } : undefined,
    });
    return response.data;
  },

  async applyLabsSubscriptionScenario(
    subscriptionId: string,
    scenario: LabsSubscriptionScenario,
  ): Promise<AdminLabsScenarioResult> {
    const response = await api.post<AdminLabsScenarioResult>(
      `/admin/labs/subscriptions/${subscriptionId}/scenario`,
      { scenario },
    );
    return response.data;
  },

  async runLabsSubscriptionMaintenance(): Promise<AdminLabsMaintenanceResult> {
    const response = await api.post<AdminLabsMaintenanceResult>(
      "/admin/labs/subscriptions/maintenance",
    );
    return response.data;
  },

  async createLabsCardPayment(body: {
    transaction_amount: number;
    description?: string;
    card: { token: string; payment_method_id?: string; issuer_id?: string };
    email?: string;
    name?: string;
    identificationType?: string;
    identificationNumber?: string;
    deviceSessionId?: string;
  }): Promise<AdminLabsCardPaymentResult> {
    const response = await api.post<AdminLabsCardPaymentResult>(
      "/admin/labs/payments/card",
      body,
    );
    return response.data;
  },

  async refundLabsCardPayment(paymentId: string): Promise<AdminLabsCardRefundResult> {
    const response = await api.post<AdminLabsCardRefundResult>(
      `/admin/labs/payments/${paymentId}/refund`,
    );
    return response.data;
  },
};

export type LabsSubscriptionScenario =
  | "reminder_7d"
  | "reminder_3d"
  | "reminder_1d"
  | "period_end_passed"
  | "period_end_cancelled"
  | "grace_expired"
  | "reset_active_month";

export type AdminLabsSubscription = {
  id: string;
  status: string;
  teamId: string;
  teamName: string;
  teamSlug?: string | null;
  userId: string;
  userEmail: string;
  userName?: string | null;
  planId: string;
  planName: string;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd: boolean;
  gracePeriodEndsAt?: string | null;
  lastReminderDays?: number | null;
  updatedAt: string;
};

export type AdminLabsScenarioResult = {
  scenario: LabsSubscriptionScenario;
  subscription: {
    id: string;
    status: string;
    teamName: string;
    userEmail: string;
    planName: string;
    currentPeriodEnd?: string | null;
    cancelAtPeriodEnd: boolean;
    gracePeriodEndsAt?: string | null;
    lastReminderDays?: number | null;
  };
};

export type AdminLabsMaintenanceResult = {
  reminders: number;
  expired: number;
  pastDue: number;
  ranAt: string;
};

export type AdminLabsCardPaymentResult = {
  paymentId: string;
  status: string;
  amount: number;
  providerPaymentId?: string | null;
  providerMovementId?: string | null;
  providerStatus?: string | null;
  providerStatusDetail?: string | null;
  providerError?: string | null;
  providerErrorMessage?: string | null;
  providerRaw?: Record<string, unknown>;
};

export type AdminLabsCardRefundResult = {
  paymentId: string;
  status: string;
  providerRefundId?: string | null;
  alreadyRefunded?: boolean;
  refundRaw?: Record<string, unknown>;
};
